import { inject, Injectable, signal } from '@angular/core';

import { SDK, STORE } from '@plugins/middleware-sdk';
import { Override } from '@plugins/shared/types';
import { augmentObject } from '@plugins/shared/utils/objectUtils/modify-object';

import { IncomingTranslation, UnflattenedTranslationType } from './types';
export abstract class TranslateExternalOverrideToken {
  abstract get hasOverrides(): boolean | null;
  abstract getOverride(lang: string): Promise<IncomingTranslation>;
}
@Injectable({ providedIn: 'root' })
export class TranslateExternalOverrideService extends TranslateExternalOverrideToken {
  protected overrideKey = Override.i18n;
  protected sdk = inject(SDK);
  protected store = inject(STORE);

  protected readonly isOverridePresent = signal<boolean | null>(null);
  protected readonly cachedOverrides = signal<UnflattenedTranslationType>({});

  get hasOverrides() {
    return this.isOverridePresent();
  }

  /**
   * Retrieves translations for a given language from both tenant and editor scope,
   * and merges them into a single object. Editor entries take precedence and
   * override tenant values when keys overlap.
   */
  private async getTenantAndEditorOverride(
    lang: string,
  ): Promise<IncomingTranslation> {
    const entries = await this.store.mget([
      { type: 'tenant', key: `${this.overrideKey}:${lang}` },
      { type: 'editor', key: `${this.overrideKey}:${lang}` },
    ]);

    const tenantValue = entries.find((e) => e.type === 'tenant')?.value ?? {};
    const editorValue = entries.find((e) => e.type === 'editor')?.value ?? {};

    return augmentObject(
      tenantValue as Record<string, unknown>,
      editorValue as Record<string, unknown>,
    );
  }

  /** Returns overrides from the middleware store, if present.
   * If no overrides are present or the current language is not
   * present or it is malformed, then an empty object is returned. */
  async getOverride(lang: string): Promise<IncomingTranslation> {
    if (this.hasOverrides === null) {
      this.isOverridePresent.set(
        await this.sdk.isOverridePresent(this.overrideKey),
      );
    }
    const empty = {} as IncomingTranslation;

    if (this.hasOverrides === false) return empty;

    const cache = this.cachedOverrides()[lang];
    if (cache) return cache;
    try {
      const translations = await this.getTenantAndEditorOverride(lang);

      this.cachedOverrides.update((prev) => ({
        ...prev,
        [lang]: translations,
      }));
      return translations;
    } catch (err) {
      console.warn(
        'An error occured while fetching translation overrides:',
        err,
      );
      return empty;
    }
  }
}

export class MockTranslateExternalOverrideService extends TranslateExternalOverrideToken {
  get hasOverrides(): boolean | null {
    return false;
  }
  getOverride(_lang: string): Promise<IncomingTranslation> {
    return Promise.resolve({});
  }
}
